/**
 * Cross-tab coordination for refresh token so only one tab performs the refresh
 * and others wait for the result. Prevents "Invalid or expired refresh token"
 * when multiple tabs load with the same (single-use) refresh token.
 *
 * Uses localStorage for a lock and BroadcastChannel (or storage event fallback)
 * to signal refresh-done / refresh-failed.
 */

const LOCK_KEY = 'auth-refresh-lock';
const LOCK_RECENT_MS = 3000;
const LOCK_ACQUIRE_WAIT_MS = 60;
const FOLLOWER_TIMEOUT_MS = 12000; // Was 15000; leader's own timeout is 10s, so 12s gives 2s headroom
const REFRESH_RESULT_KEY = 'auth-refresh-result';
const CHANNEL_NAME = 'scaffolder-auth-refresh';

function getLock() {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const raw = localStorage.getItem(LOCK_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function setLock(value) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        if (value == null) {
            localStorage.removeItem(LOCK_KEY);
        } else {
            localStorage.setItem(LOCK_KEY, JSON.stringify(value));
        }
    } catch (_) { /* ignore */ }
}

function isLockRecent(lock) {
    if (!lock || typeof lock.t !== 'number') return false;
    return lock.t > Date.now() - LOCK_RECENT_MS;
}

/**
 * Remove the lock if it is stale (older than LOCK_RECENT_MS).
 * Call this at the start of refresh so we don't wait for a tab that closed.
 */
export function clearStaleLock() {
    if (typeof window === 'undefined') return;
    const lock = getLock();
    if (lock && !isLockRecent(lock)) {
        setLock(null);
    }
}

/**
 * Try to become the "leader" tab that performs the refresh.
 * @returns {Promise<{ acquired: boolean }>} - acquired true if this tab should perform the refresh
 */
export function tryAcquireLock() {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve({ acquired: true });
            return;
        }
        const lock = getLock();
        if (lock && isLockRecent(lock)) {
            resolve({ acquired: false });
            return;
        }
        const myId = Math.random();
        setLock({ t: Date.now(), id: myId });
        setTimeout(() => {
            const current = getLock();
            if (current && current.id === myId && isLockRecent(current)) {
                resolve({ acquired: true });
            } else {
                resolve({ acquired: false });
            }
        }, LOCK_ACQUIRE_WAIT_MS);
    });
}

export function releaseLock() {
    setLock(null);
}

/**
 * Wait for another tab to finish refresh (refresh-done or refresh-failed).
 * @returns {Promise<{ accessToken: string, accessTokenExpiresAt: number, user?: object }>}
 * @throws on refresh-failed or timeout
 */
export function waitForOtherTabRefresh() {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('No window'));
            return;
        }
        const timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error('Token refresh timeout'));
        }, FOLLOWER_TIMEOUT_MS);

        function cleanup() {
            clearTimeout(timeoutId);
            if (channel) channel.close();
            window.removeEventListener('storage', onStorage);
        }

        function done(payload) {
            cleanup();
            resolve(payload);
        }

        function fail() {
            cleanup();
            reject(new Error('Session expired'));
        }

        const onMessage = (event) => {
            const data = event?.data;
            if (!data || data.type === undefined) return;
            if (data.type === 'refresh-done') {
                if (data.accessToken != null && data.accessTokenExpiresAt != null) {
                    done({
                        accessToken: data.accessToken,
                        accessTokenExpiresAt: data.accessTokenExpiresAt,
                        user: data.user ?? undefined
                    });
                } else {
                    fail();
                }
            } else if (data.type === 'refresh-failed') {
                fail();
            }
        };

        const onStorage = (e) => {
            if (e.key !== REFRESH_RESULT_KEY || e.newValue == null) return;
            try {
                const payload = JSON.parse(e.newValue);
                if (payload.success && payload.accessToken != null && payload.accessTokenExpiresAt != null) {
                    done({
                        accessToken: payload.accessToken,
                        accessTokenExpiresAt: payload.accessTokenExpiresAt,
                        user: payload.user
                    });
                } else {
                    fail();
                }
            } catch (_) {
                fail();
            }
        };

        let channel = null;
        if (typeof BroadcastChannel !== 'undefined') {
            try {
                channel = new BroadcastChannel(CHANNEL_NAME);
                channel.onmessage = onMessage;
            } catch (_) {
                channel = null;
            }
        }
        window.addEventListener('storage', onStorage);
    });
}

/**
 * Broadcast refresh success to other tabs. Clears lock.
 * @param {{ accessToken: string, accessTokenExpiresAt: number, user?: object }} payload
 */
export function broadcastRefreshDone(payload) {
    releaseLock();
    if (typeof window === 'undefined') return;
    if (typeof BroadcastChannel !== 'undefined') {
        try {
            const ch = new BroadcastChannel(CHANNEL_NAME);
            ch.postMessage({
                type: 'refresh-done',
                accessToken: payload.accessToken,
                accessTokenExpiresAt: payload.accessTokenExpiresAt,
                user: payload.user
            });
            ch.close();
        } catch (_) { /* ignore */ }
    }
    try {
        localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify({
            success: true,
            accessToken: payload.accessToken,
            accessTokenExpiresAt: payload.accessTokenExpiresAt,
            user: payload.user
        }));
        setTimeout(() => {
            try { localStorage.removeItem(REFRESH_RESULT_KEY); } catch (_) { }
        }, 500);
    } catch (_) { /* ignore */ }
}

/**
 * Broadcast refresh failure to other tabs. Clears lock.
 */
export function broadcastRefreshFailed() {
    releaseLock();
    if (typeof window === 'undefined') return;
    if (typeof BroadcastChannel !== 'undefined') {
        try {
            const ch = new BroadcastChannel(CHANNEL_NAME);
            ch.postMessage({ type: 'refresh-failed' });
            ch.close();
        } catch (_) { /* ignore */ }
    }
    try {
        localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify({ success: false }));
        setTimeout(() => {
            try { localStorage.removeItem(REFRESH_RESULT_KEY); } catch (_) { }
        }, 500);
    } catch (_) { /* ignore */ }
}

/**
 * Check if another tab is currently refreshing (we should wait for it).
 */
export function isOtherTabRefreshing() {
    const lock = getLock();
    return !!(lock && isLockRecent(lock));
}
